"use client";

import { useEffect } from "react";

import { resetForLogout } from "@repo/store";
import { useDispatch } from "react-redux";

import { persistor } from "@repo/store/persistor";

import { consumeLogoutProcessed } from "./logoutSignal";

/**
 * Clears the LIVE store after a logout, which clearing `localStorage` alone cannot do.
 *
 * `ReduxInitializer` removes the persisted keys, but by then redux-persist has already
 * rehydrated the old per-tab blob into memory - and the root reducer's REHYDRATE branch
 * explicitly carries `auth` across, while `resetToMain` (dispatched on every main load)
 * explicitly preserves it. The tab session id then rotates and persist writes that
 * still-authenticated state straight back out under the new key, so the header keeps
 * rendering the previous user. This is why logging out from MAIN looked fine while
 * logging out from a zone did not: from main, the store was deleted before the
 * navigation, so there was nothing left to rehydrate.
 *
 * Must live inside ReduxProvider's PersistGate, which renders children only after
 * rehydration completes - so this dispatch always lands after REHYDRATE rather than
 * racing it.
 */
export function LogoutStateReset() {
  const dispatch = useDispatch();

  useEffect(() => {
    // NOTE: React invokes effects twice in dev, so this runs twice per load. The signal is
    // consumed on the first pass, which is why the second is a no-op rather than a second
    // reset - do not "fix" that by re-reading the URL, which would make it fire on reloads.
    if (!consumeLogoutProcessed()) {
      return;
    }

    dispatch(resetForLogout());
    // write the cleaned state out now, rather than waiting for the next persist tick
    void persistor.flush();
  }, [dispatch]);

  return null;
}
